<template>
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create schools')"
        @click="showSchoolLocationModal(0)"
        color="primary"
        dark
        class="mb-2"
      >{{ $t("NEW") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.school_code="{ item }">
            {{ (item.options.branch_code) ? item.options.branch_code : item.school_code }}
      </template>

      <template v-slot:item.propagandist="{ item }">
        <router-link
            v-if="can('show propagandists')"
            :to="{ name: routeName.PROPAGANDIST_SHOW, params: { id: item.propagandist_id } }"
        >
            {{ item.propagandist }}
        </router-link>
        <span v-else>{{ item.propagandist }}</span>
      </template>

      <template v-slot:item.actions="{ item }">
            <v-layout wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit schools')"
                  @click="showSchoolLocationModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('delete schools')"
                  icon
                  color="red"
                  @click="deleteSchoolLocation(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchSchoolLocations"
    />
  </div>
</template>

<script>
  import { mapActions } from 'vuex';

  import { schoolsLocationsService } from '../../../utils/services/schoolsLocations.service';
  import SchoolLocationModal from '../../modals/Schools/SchoolLocationModal';
  import Paginator from '../../shared/Paginator';

  export default {
    name: 'SchoolLocationsDetails',
    components: { Paginator },
    props: {
      school: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['code'],
        },
        headers: [
          {
            text: this.$t('fields.school_code'),
            align: 'left',
            value: 'school_code',
          },
          { text: this.$t('fields.code'), value: 'code' },
          { text: this.$t('fields.city'), value: 'city' },
          { text: this.$t('fields.phone'), value: 'phone' },
          { text: this.$t('fields.email'), value: 'email' },
          { text: this.$t('fields.address'), value: 'street' },
          { text: this.$t('fields.propagandist'), value: 'propagandist' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
      };
    },
    computed: {
      requestFilters() {
        const filters = {};
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),

      async showSchoolLocationModal(id) {
        this.showModal({
          component: SchoolLocationModal,
            data: {
              stored: {
                id,
                school_id: this.school.id,
              },
          },
          options: {
            title: this.$t('SCHOOL_LOCATION'),
            onSave: (
              form,
              error,
            ) => schoolsLocationsService.save(form)
            .then(() => {
              this.fetchSchoolLocations(this.pagination.page);
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchSchoolLocations(this.pagination.page);
            }),
          },
        });
      },

      async fetchSchoolLocations(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return schoolsLocationsService.index(this.school.id, {
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
        .then((response) => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
        })
        .catch(error => {
          this.handleError(error, this.errors);
        })
        .finally(() => {
          this.loading = false;
        });
      },

      async deleteSchoolLocation(id) {
          this.showConfirm({
            message: this.$t('error_dissociate_propagandist_before_remove_school_address'),
            onConfirm: () => {
              this.loading = true;
              return schoolsLocationsService.remove(this.school.id, id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchSchoolLocations(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.error);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchSchoolLocations();
    },
  };
</script>
